
import React, { useState, useRef, useCallback, memo } from "react";
import { RNCheckBoxTree } from "../../../components";
import { useClickOutside } from "../utils/eventListeners";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import Box from "@mui/material/Box";

interface RegionFilterProps {
    hierarchy: any;
    selectedLevel1: string[];
    onSelectionChange: (nodes: string[]) => void;
}

export const RegionFilter: React.FC<RegionFilterProps> = memo(({
    hierarchy,
    selectedLevel1,
    onSelectionChange,
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const dropdownRef = useRef<HTMLDivElement>(null);

    // Memoize the close handler to prevent useClickOutside from re-running
    const handleClose = useCallback(() => setIsOpen(false), []);
    useClickOutside(dropdownRef, handleClose);

    // Memoize toggle handler
    const handleToggle = useCallback(() => setIsOpen(prev => !prev), []);

    // Memoize remove handler for individual chips
    const handleRemoveLevel = useCallback((indexToRemove: number) => {
        onSelectionChange(selectedLevel1.filter((_, i) => i !== indexToRemove));
    }, [selectedLevel1, onSelectionChange]);

    // Memoize clear all handler
    const handleClearAll = useCallback(() => {
        onSelectionChange([]);
    }, [onSelectionChange]);

    return (
        <div className="relative" ref={dropdownRef}>
            <Button
                onClick={handleToggle}
                fullWidth
                variant="outlined"
                color={selectedLevel1.length > 0 ? "primary" : "inherit"}
                sx={{
                    backgroundColor: selectedLevel1.length > 0 ? "rgba(99, 102, 241, 0.1)" : "rgb(248, 250, 252)",
                    borderColor: selectedLevel1.length > 0 ? "rgba(99, 102, 241, 0.3)" : "rgb(226, 232, 240)",
                    color: selectedLevel1.length > 0 ? "rgb(99, 102, 241)" : "rgb(51, 65, 85)",
                }}
            >
                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1, width: "100%" }}>
                    <Box sx={{ display: "flex", flex: 1, alignItems: "center", gap: 0.5, flexWrap: "wrap", minHeight: "24px" }}>
                        {selectedLevel1.length > 0 ? (
                            selectedLevel1.map((level, idx) => (
                                <Chip
                                    key={level}
                                    label={level}
                                    size="small"
                                    onDelete={(e) => {
                                        e.stopPropagation();
                                        handleRemoveLevel(idx);
                                    }}
                                />
                            ))
                        ) : (
                            <span style={{ color: "rgb(100, 116, 139)", fontSize: "0.875rem" }}>Select regions...</span>
                        )}
                    </Box>
                    <i
                        className={`fas fa-chevron-${isOpen ? "up" : "down"}`}
                        style={{ fontSize: "0.75rem", color: "rgb(148, 163, 184)", flexShrink: 0 }}
                    />
                </Box>
            </Button>

            {isOpen && (
                <div className="absolute top-full left-0 right-0 mt-1 bg-white rounded-lg border border-slate-200 shadow-lg z-50 max-h-96 overflow-y-auto">
                    <div className="p-4">
                        <div className="mb-3 flex items-center justify-between">
                            <span className="text-sm font-semibold text-slate-900">
                                Select Regions
                            </span>
                            {selectedLevel1.length > 0 && (
                                <Button
                                    onClick={handleClearAll}
                                    size="small"
                                    variant="text"
                                    color="error"
                                >
                                    Clear All
                                </Button>
                            )}
                        </div>
                        <div className="max-h-80 overflow-y-auto">
                            <RNCheckBoxTree
                                data={hierarchy}
                                on_node_selection={onSelectionChange}
                                checked_nodes={selectedLevel1}
                            />
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
});

